/**
 * Переключатель-сегменты: один выбранный из нескольких, подложка едет к нему.
 *
 * Именно переключатель, а не вкладки. Вкладки говорят «здесь есть ещё
 * страницы»; сегменты — «это один и тот же список, показанный по-разному».
 * Для площадок верно второе: владелец не уходит в другой раздел, он сужает
 * взгляд.
 *
 * Подложка — отдельный элемент, который двигается transform'ом, а не фон
 * активной кнопки. Так переход виден как движение, и глаз не теряет, откуда
 * куда переехало. При `prefers-reduced-motion` движение выключается: смысл
 * несёт положение, а не анимация.
 */
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Hint } from './Hint.tsx';

export interface SegmentedItem {
  id: string;
  label: string;
  /** Значок системы слева от подписи. Опознаёт, а не украшает. */
  icon?: ReactNode;
  /**
   * Приглушённый вид: площадка объявлена, но там ещё ничего нет.
   *
   * Не то же, что «выключено». Пустая площадка, о которой известно, что она
   * понадобится, — часть картины: убрать её значит забыть про задачу, а
   * показать наравне с работающими — соврать, что она работает.
   */
  muted?: boolean;
  /** Число справа от подписи: сколько объектов внутри. */
  count?: number;
  /** Точка: внутри есть то, что требует вмешательства. */
  alert?: boolean;
  /** Вопрос: сюда консоль не дотягивается и знать не может. */
  blind?: boolean;
  title?: string;
  /**
   * Значок канала данных и подсказка к нему.
   *
   * Отдельно от `title`, потому что это не пояснение к вкладке, а ответ на
   * другой вопрос: откуда мы знаем то, что показано внутри. Между «копий нет»
   * и «мы не видим» разница решающая, и держать её в голове не должен никто.
   */
  channel?: { glyph: string; state: string; hint: ReactNode };
}

export function Segmented({
  items,
  active,
  onSelect,
  ariaLabel,
  size = 'md',
}: {
  items: SegmentedItem[];
  active: string;
  onSelect: (id: string) => void;
  ariaLabel: string;
  size?: 'md' | 'sm';
}): React.JSX.Element {
  const listRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);

  // Положение меряется после отрисовки, а не считается по числу элементов:
  // подписи разной длины, и сегменты не равны по ширине. Считать «шириной
  // делить на количество» значило бы промахиваться тем сильнее, чем длиннее
  // названия площадок.
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const measure = (): void => {
      const node = list.querySelector<HTMLElement>(`[data-seg="${CSS.escape(active)}"]`);
      if (!node) {
        setPill(null);
        return;
      }
      setPill({ left: node.offsetLeft, width: node.offsetWidth });
    };

    measure();

    // Ширина меняется от шрифтов, которые догрузились, и от поворота экрана.
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [active, items.map((i) => i.id + i.label + String(i.count)).join('|')]);

  const move = (delta: number): void => {
    const index = items.findIndex((i) => i.id === active);
    const next = items[(index + delta + items.length) % items.length];
    if (next) onSelect(next.id);
  };

  return (
    <div
      ref={listRef}
      className={`seg seg--${size}`}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
      }}
    >
      {pill && (
        <span
          className="seg__pill"
          aria-hidden="true"
          style={{ transform: `translateX(${pill.left}px)`, width: `${pill.width}px` }}
        />
      )}
      {items.map((item) => (
        <span key={`seg-${item.id}`} className="seg__slot">
        <button
          type="button"
          role="tab"
          data-seg={item.id}
          aria-selected={active === item.id}
          tabIndex={active === item.id ? 0 : -1}
          className={
            `seg__item${active === item.id ? ' seg__item--active' : ''}` +
            `${item.muted ? ' seg__item--muted' : ''}`
          }
          onClick={() => onSelect(item.id)}
          title={item.title}
        >
          {item.icon && <span className="seg__icon" aria-hidden="true">{item.icon}</span>}
          <span className="seg__label">{item.label}</span>
          {item.count !== undefined && <span className="seg__count">{item.count}</span>}
          {item.alert && <span className="seg__dot" aria-label="требует внимания" />}
          {item.blind && <span className="seg__blind" title="Сюда консоль не дотягивается">?</span>}
        </button>
        {item.channel && (
          <Hint
            className={`seg__channel seg__channel--${item.channel.state}`}
            content={item.channel.hint}
          >
            <span aria-hidden="true">{item.channel.glyph}</span>
          </Hint>
        )}
        </span>
      ))}
    </div>
  );
}
